/* eslint-disable no-nested-ternary */
/* eslint-disable indent */
/* eslint-disable operator-assignment */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  differenceInMonths,
  isSameMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  endOfDay,
  format,
  formatISO,
  isBefore,
  startOfDay,
  subDays,
} from 'date-fns';
import Report from '../../../../componentsV2/Layout/Report';
import { addLoadingList, removeLoadingList, setLoadingDescription } from '../../../../reducers/notification';
import NewClientExpandRow from './components/NewClientsExpandRow';
import useSearchParams from '../../../../hooks/useSearchParams';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';
import { useReport } from '../../../../context/ReportContext';

const ADDED_MANUALLY = new Set(['APP']);
const ADDED_POS = new Set(['POS']);

const NewClients = ({ auth }) => {
  // Web Worker initialization
  const workerRef = useRef(null);
  const [processedData, setProcessedData] = useState({
    dataByClientId: [],
    dataBySales: [],
    stats: [],
    graphData: {},
    totalCount: 0,
  });

  const options = useMemo(
    () => [
      {
        title: 'Source',
        key: '0-0',
        value: 'sourceMethod',
        children: [
          {
            key: '0-0-0',
            title: 'Webchat & Inbound',
            value: 'FROM_INBOUND',
          },
          {
            key: '0-0-1',
            title: 'Added Manually',
            value: 'MANUALLY',
          },
          {
            key: '0-0-1',
            title: 'Added POS',
            value: 'POS',
          },
          {
            key: '0-0-2',
            title: 'Kiosk',
            value: 'KIOSK',
          },
        ],
      },
    ],
    [],
  );
  const [filters, setFilters] = useState({
    sourceMethod: [],
    store: [],
    associate: [],
  });
  const [dateRange, setDateRange] = useState([subDays(new Date(), 90), new Date()]);

  const { setValue: setSearchParams, getAll } = useSearchParams();
  const params = getAll();
  const { newClients, isLoadingNewClients, newClientsMeta, isLoadingNewClientsMeta } = useReport();

  // Memoize auth values to prevent unnecessary worker reprocessing
  const authStores = useMemo(() => auth.stores, [auth.stores]);
  const hasPosType = useMemo(() => !!auth?.pos_type, [auth?.pos_type]);

  // Initialize Web Worker
  useEffect(() => {
    workerRef.current = new Worker(new URL('./newClientsWorker.js', import.meta.url));

    workerRef.current.onmessage = (e) => {
      if (e.data.type === 'DATA_PROCESSED') {
        setProcessedData(e.data.payload);
      } else if (e.data.type === 'PROCESSING_ERROR') {
        console.error('Worker processing error:', e.data.error);
        removeLoadingList('newClientsReport');
        setLoadingDescription(null);
      }
    };

    return () => workerRef.current?.terminate();
  }, []);

  // Remove loading spinner after data has been rendered
  useEffect(() => {
    if (processedData.totalCount !== undefined) {
      removeLoadingList('newClientsReport');
      setLoadingDescription(null);
    }
  }, [processedData]);

  useEffect(() => {
    const paramsFilters = {
      sourceMethod: params?.sourceMethod ? params?.sourceMethod : [],
      store: params?.store ? params?.store : [],
      associate: params?.associate ? params?.associate : [],
    };
    setFilters(paramsFilters);
    if (params?.startDate && params?.endDate) {
      setDateRange([new Date(params.startDate), new Date(params.endDate)]);
    } else {
      setDateRange([startOfDay(subDays(new Date(), 90)), endOfDay(new Date())]);
    }
  }, [window.location.search]);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSizeTable, setPageSizeTable] = useState(10);
  const [page, setPage] = useState(1);

  const getDefaultPeriod = useMemo(() => {
    if (params?.startDate && params?.endDate) {
      if (
        isSameMonth(new Date(params.endDate), new Date(params.startDate)) &&
        isFirstDayOfMonth(new Date(params.startDate)) &&
        isLastDayOfMonth(new Date(params.endDate)) &&
        differenceInMonths(new Date(), new Date(params.startDate)) === 1
      ) {
        return 'previousmonth';
      }
      return `${format(new Date(params.startDate), 'MM/dd/yyyy')} - ${format(new Date(params.endDate), 'MM/dd/yyyy')}`;
    }
    return 'previousmonth';
  }, []);
  const handleOnDateRangeChange = useCallback(dates => {
    // Start loading spinner immediately
    setLoadingDescription("Sit tight! We're compiling your report.");
    addLoadingList('newClientsReport');

    setSearchParams('startDate', formatISO(dates[0]));
    setSearchParams('endDate', formatISO(dates[1]));
  }, []);

  // Trigger data processing in Web Worker
  useEffect(() => {
    if (!newClients || !newClientsMeta || isLoadingNewClients || isLoadingNewClientsMeta) {
      if (isLoadingNewClients || isLoadingNewClientsMeta) {
        setLoadingDescription("Sit tight! We're compiling your report.");
        addLoadingList('newClientsReport');
      }
      return;
    }

    setLoadingDescription("Sit tight! We're compiling your report.");
    addLoadingList('newClientsReport');
    setPage(1); // Reset page when data changes

    workerRef.current.postMessage({
      type: 'PROCESS_DATA',
      payload: {
        newClients,
        newClientsMeta,
        filters,
        dateRange,
        assocStores: authStores,
        hasPosType,
      },
    });
  }, [newClients, newClientsMeta, filters, dateRange, authStores, hasPosType]);


  const handleFilterChange = useCallback(
    value => {
      // Start loading spinner immediately
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('newClientsReport');

      const updatedFilters = {
        sourceMethod: [],
        store: [],
        associate: [],
      };
      value.forEach(item => {
        if (item[0] && item[1]) {
          updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
        } else {
          updatedFilters[item[0]] = [];
        }
      });
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  const removeFilter = useCallback(
    (key, value) => {
      // Start loading spinner immediately
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('newClientsReport');

      const updatedFilters = { ...filters };
      updatedFilters[key] = updatedFilters[key].filter(filter => filter !== value);
      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  // Convert worker stats format back to JSX for rendering
  const stats = useMemo(() => {
    return processedData.stats.map(stat => ({
      ...stat,
      description: stat.description.height ? (
        <div className={stat.description.className} style={{ height: stat.description.height }}>
          {stat.description.text}
        </div>
      ) : (
        <span className={stat.description.className}>{stat.description.text}</span>
      ),
    }));
  }, [processedData.stats]);

  const columns = useMemo(() => {
    const relatedSales = hasPosType
      ? [
          {
            title: 'Related Sales',
            dataIndex: 'related_sales',
            key: 'related_sales',
            sorter: (a, b) => a.related_sales - b.related_sales,
            render: amount => (amount ? formatNumberAsCurrency(amount) : '-'),
          },
        ]
      : [];
    return [
      {
        title: 'Added on',
        dataIndex: 'activity_date',
        key: 'activity_date',
        defaultSortOrder: 'descend',
        sorter: (a, b) => {
          const dateA = new Date(a.activity_date);
          const dateB = new Date(b.activity_date);

          return dateA - dateB;
        },
        render: value => format(new Date(value), 'MMM dd, yyyy'),
      },
      {
        title: 'Client',
        dataIndex: 'client_name',
        key: 'client_name',
        sorter: (a, b) => a.client_name.localeCompare(b.client_name),
        render: (text, record) => (
          <a href={`/Clients?client=${record.client_id}`} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ),
      },
      {
        title: 'Added by',
        dataIndex: 'activity_associate_name',
        key: 'activity_associate_name',
        sorter: (a, b) => {
          const textA = a?.activity_associate_name?.toUpperCase() || 'zzzzzzzz';
          const textB = b?.activity_associate_name?.toUpperCase() || 'zzzzzzzz';
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        },
      },
      {
        title: 'Source',
        dataIndex: 'activity_source',
        key: 'activity_source',
        sorter: (a, b) => a.activity_source.localeCompare(b.activity_source),
        render: value =>
          ADDED_MANUALLY.has(value)
            ? 'Added Manually'
            : ADDED_POS.has(value)
            ? 'Added from POS '
            : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
      },
      ...relatedSales,
    ];
  }, [hasPosType]);

  const expandedRowRender = useCallback(record => {
    return <NewClientExpandRow record={record} licenseKey={auth.licenseKey} />;
  }, []);

  const handleExpandRow = useCallback(
    (expand, record) => {
      let updatedExpandedRowKeys = [...expandedRowKeys];
      if (expand) {
        updatedExpandedRowKeys.push(record.client_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      } else {
        updatedExpandedRowKeys = updatedExpandedRowKeys.filter(key => key !== record.client_id);
        setExpandedRowKeys(updatedExpandedRowKeys);
      }
    },
    [expandedRowKeys],
  );

  return (
    <Report
      title="New Clients"
      stats={stats}
      options={options}
      hasCSVExport
      CSVFilename="new-clients.csv"
      handleFilterChange={handleFilterChange}
      filters={filters}
      removeFilter={removeFilter}
      onDateRangeChange={handleOnDateRangeChange}
      dataRange={dateRange}
      defaultPeriodValue={getDefaultPeriod}
      graphData={processedData.graphData}
      maxStatsPerLine={3}
      stacked
      columns={columns}
      table={processedData.dataByClientId}
      hasBanner={isBefore(dateRange[0], new Date('2024-04-25'))}
      textBanner="Clientbook started to track that information as of April 25th, 2024. You are unable to see the information before that date."
      tablesProps={{
        pagination: {
          total: processedData.dataByClientId.length,
          showSizeChanger: true,
          showTotal: total => `Total ${total} items`,
          onChange: (page, pageSize) => {
            setPage(page);
            setPageSizeTable(pageSize);
          },
          page,
          pageSize: pageSizeTable,
        },
        rowKey: 'client_id',
        expandable: {
          expandedRowRender: record => expandedRowRender(record),
          defaultExpandedRowKeys: ['0'],
          onExpand: (expanded, record) => handleExpandRow(expanded, record),
          expandedRowKeys,
          expandRowByClick: true,
        },
      }}
    />
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(NewClients);
